import css from './Wrapper.module.scss'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { BalloonBgPosition, BalloonSize, BalloonTipPosition, ComponentColor } from 'meta/backendControlled/structure'
import { Breakpoints } from 'meta/layout/breakpoints'
import { MAX_BULK_REQUEST_SIZE } from 'meta/pages/v4/bulk'
import { useAppDispatch } from 'store'
import { CheckoutActions } from 'store/pages/checkout'
import { CollectionActions } from 'store/pages/collection'
import { LocationActions } from 'store/pages/location'
import { chunkArrayInGroups } from 'utils/array'

import Menu, { MenuSkeleton } from 'components/BackendControlled/Component/Menu'
import { GridColumn, GridContainer } from 'components/Layout/Grid'
import {
  useCollectionActiveEvents,
  useCollectionActiveTypeticketcollections,
  useCollectionDetails,
} from 'hooks/useCollection'
import { useCurrentLanguage } from 'hooks/useCurrentLanguage'

import Artist from '../Artist'
import LineupModal from '../Artist/LineupModal'
import CollectionBalloon from '../Balloon/CollectionBalloon'
import EventDataFetcher from '../BulkDataFetcher/EventDataFetcher'
import PerformerDataFetcher from '../BulkDataFetcher/PerformerDataFetcher'
import TypeticketcollectionDataFetcher from '../BulkDataFetcher/TypeticketcollectionDataFetcher'
import CollectionDetails from '../CollectionDetails'
import CollectionDetailsSkeleton from '../CollectionDetails/CollectionDetailsSkeleton'
import CollectionHead from '../CollectionHead'
import GoBack from '../CollectionIFrame/GoBack/GoBack'
import EventList from '../EventList/EventList'
import EventListSkeleton from '../EventList/EventListSkeleton'
import InfoBox from '../InfoBox'
import SeasonList from '../SeasonList/SeasonList'
import { getCollectionsMenuItems } from './utils'

interface Props {
  id: string
  fromIframe?: boolean
}

const Wrapper: React.FC<Props> = (props) => {
  const { id, fromIframe } = props

  const { t } = useTranslation()

  const language = useCurrentLanguage()

  const details = useCollectionDetails({ id, language })
  const activeEvents = useCollectionActiveEvents({ id, language })
  const activeTypeticketcollections = useCollectionActiveTypeticketcollections({ id, language })

  const [showContent, setShowContent] = useState<boolean>(true)
  const [showLineupModal, setShowLineupModal] = useState<boolean>(false)

  const dispatch = useAppDispatch()

  const toggleContent = () => {
    dispatch(CheckoutActions.close())
    setShowContent(!showContent)
  }

  useEffect(() => {
    if (details && details.locations && details.locations.items && details.locations.items.length > 0) {
      dispatch(
        LocationActions.getData({
          idOrCodeUrl: details.locations.items[0]?.id.toString(),
        })
      )
    }
  }, [details])

  useEffect(() => {
    if (activeEvents && activeEvents.metadata && activeEvents.metadata.hasMorePages) {
      dispatch(
        CollectionActions.getActiveEvents({
          collectionId: id,
          page: activeEvents.metadata.page + 1,
        })
      )
    }
  }, [activeEvents])

  useEffect(() => {
    if (
      activeTypeticketcollections &&
      activeTypeticketcollections.metadata &&
      activeTypeticketcollections.metadata.hasMorePages
    ) {
      dispatch(
        CollectionActions.getActiveTypeticketcollections({
          collectionId: id,
          page: activeTypeticketcollections.metadata.page + 1,
        })
      )
    }
  }, [activeTypeticketcollections])

  return (
    <div className={classNames(css.collectionWrapper, { [css.fromIframe]: Boolean(fromIframe) })}>
      {details && activeTypeticketcollections && (
        <CollectionHead details={details} activeTypeticketCollections={activeTypeticketcollections} />
      )}
      {!showContent && (
        <GridContainer>
          <GridColumn gridColumnStart={1} gridColumnEnd={{ [Breakpoints.tablet]: 17, [Breakpoints.mobile]: 17 }}>
            <GoBack toggleContent={toggleContent} />
          </GridColumn>
        </GridContainer>
      )}
      {showContent && (
        <GridContainer>
          <GridColumn gridColumnStart={1} gridColumnEnd={{ [Breakpoints.tablet]: 17, [Breakpoints.mobile]: 17 }}>
            {!fromIframe && (
              <CollectionBalloon
                className={css.balloonWrapper}
                backgroundUrl={details && details.assets.find((x) => x.format == 'BACKGROUND')?.url}
                color={ComponentColor.black}
                size={BalloonSize.medium}
                backgroundPosition={BalloonBgPosition.center}
                tipPosition={BalloonTipPosition.right}
                containsMenu
              >
                {details && activeEvents && activeTypeticketcollections && (
                  <>
                    <CollectionDetails
                      fbPixel={details.metaPixels[0]?.pixel ?? null}
                      googleTags={details.googleTags?.map((x) => x.tag)}
                      name={details.name}
                      id={id}
                      cover={{
                        imgUrl: details.assets?.find((x) => x.format == 'SQUARE')?.url,
                        name: details.name,
                      }}
                      header={{
                        title: details.name,
                        tags: details.tags,
                        locationId: details.locations?.items[0]?.id.toString() || '',
                      }}
                    />
                    {
                      <Menu
                        underlineFirst
                        insideBalloon
                        isSticky
                        list={getCollectionsMenuItems(details, activeEvents, activeTypeticketcollections, t)}
                      />
                    }
                  </>
                )}

                {!details && (
                  <>
                    <CollectionDetailsSkeleton />
                    <MenuSkeleton list={[]} />
                  </>
                )}
              </CollectionBalloon>
            )}
            {details && (
              <>
                {details.lineup && details.lineup.length > 0 && !fromIframe && (
                  <Artist
                    anchorId={'id_artisti'}
                    codes={details.lineup.map((x) => x.code)}
                    setShowLineupModal={setShowLineupModal}
                  />
                )}

                {activeTypeticketcollections && activeTypeticketcollections.data && (
                  <SeasonList
                    anchorId={'id_abbonamenti'}
                    collectionId={id}
                    typeticketCollectionIds={Object.values(activeTypeticketcollections.data)
                      .map((x) => x.items.map((y) => y.id))
                      .flat()}
                    fromIframe={Boolean(fromIframe)}
                    toggleContent={toggleContent}
                  />
                )}

                {activeEvents && activeEvents.data && (
                  <EventList
                    anchorId={'id_eventi'}
                    eventCodes={Object.values(activeEvents.data)
                      .map((x) => x.items.map((y) => y.code))
                      .flat()}
                    fromIframe={Boolean(fromIframe)}
                    toggleContent={toggleContent}
                  />
                )}
              </>
            )}
            {!details && <EventListSkeleton />}
          </GridColumn>
        </GridContainer>
      )}
      {details && !fromIframe && (
        <InfoBox
          galleryData={details.assets?.filter((x) => x.format == 'GALLERY')}
          descData={{
            name: details.name,
            description: details.longDescription,
            social: details.links,
          }}
          anchorId={'id_info'}
          bgImgUrl={details.assets?.filter((x) => x.format == 'GALLERY')[0]?.url}
        />
      )}

      {showLineupModal && activeEvents?.data && details?.lineup && (
        <LineupModal
          setShowLineupModal={setShowLineupModal}
          eventCodes={Object.values(activeEvents.data)
            .map((x) => x.items.map((y) => y.code))
            .flat()}
          performerCodes={details.lineup?.map((x) => x.code) || []}
        />
      )}

      {details &&
        chunkArrayInGroups(details.lineup?.map((x) => x.code) || [], MAX_BULK_REQUEST_SIZE).map((codes, key) => (
          <PerformerDataFetcher key={key} codes={codes} />
        ))}

      {activeEvents &&
        Object.values(activeEvents.data)?.map((el) => {
          const codes = el.items.map((x) => x.code)
          return chunkArrayInGroups(codes, MAX_BULK_REQUEST_SIZE).map((codes, key) => (
            <EventDataFetcher key={key} eventIds={codes} skipLocation />
          ))
        })}

      {activeTypeticketcollections &&
        Object.values(activeTypeticketcollections.data)?.map((el) => {
          const ids = el.items.map((x) => x.id)
          return chunkArrayInGroups(ids, MAX_BULK_REQUEST_SIZE).map((ids, key) => (
            <TypeticketcollectionDataFetcher key={key} typeticketIds={ids} />
          ))
        })}
    </div>
  )
}

export default Wrapper
